'use client'

import * as SliderPrimitive from '@radix-ui/react-slider'
import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '../lib/cn'

const sliderVariants = cva(
  'relative flex w-full touch-none select-none items-center data-[disabled]:opacity-50 data-[orientation=vertical]:h-full data-[orientation=vertical]:min-h-40 data-[orientation=vertical]:w-auto data-[orientation=vertical]:flex-col',
  {
    variants: {
      size: {
        sm: '',
        default: '',
        lg: '',
      },
    },
    defaultVariants: {
      size: 'default',
    },
  },
)

const trackVariants = cva('relative grow overflow-hidden rounded-full bg-muted', {
  variants: {
    size: {
      sm: 'data-[orientation=horizontal]:h-1 data-[orientation=vertical]:w-1',
      default: 'data-[orientation=horizontal]:h-1.5 data-[orientation=vertical]:w-1.5',
      lg: 'data-[orientation=horizontal]:h-2 data-[orientation=vertical]:w-2',
    },
  },
  defaultVariants: {
    size: 'default',
  },
})

const thumbVariants = cva(
  'relative block shrink-0 rounded-full border-2 border-primary bg-card shadow-sm transition-[color,box-shadow] select-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 hover:ring-2 hover:ring-ring/30 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      size: {
        sm: 'size-3.5',
        default: 'size-4',
        lg: 'size-5',
      },
    },
    defaultVariants: {
      size: 'default',
    },
  },
)

export interface SliderProps
  extends React.ComponentProps<typeof SliderPrimitive.Root>, VariantProps<typeof sliderVariants> {}

const Slider = React.forwardRef<React.ComponentRef<typeof SliderPrimitive.Root>, SliderProps>(
  ({ className, size, defaultValue, value, min = 0, max = 100, ...props }, ref) => {
    const values = React.useMemo(
      () => (Array.isArray(value) ? value : Array.isArray(defaultValue) ? defaultValue : [min, max]),
      [value, defaultValue, min, max],
    )

    return (
      <SliderPrimitive.Root
        ref={ref}
        data-slot='slider'
        defaultValue={defaultValue}
        value={value}
        min={min}
        max={max}
        className={cn(sliderVariants({ size, className }))}
        {...props}
      >
        <SliderPrimitive.Track data-slot='slider-track' className={cn(trackVariants({ size }))}>
          <SliderPrimitive.Range
            data-slot='slider-range'
            className='absolute bg-primary select-none data-[orientation=horizontal]:h-full data-[orientation=vertical]:w-full'
          />
        </SliderPrimitive.Track>
        {values.map((_, i) => (
          <SliderPrimitive.Thumb
            key={i}
            data-slot='slider-thumb'
            aria-label='Slider thumb'
            className={cn(thumbVariants({ size }))}
          />
        ))}
      </SliderPrimitive.Root>
    )
  },
)
Slider.displayName = 'Slider'

export { Slider, sliderVariants }
